import React, { useState, useRef, useCallback } from 'react';
import { Case } from '../types';
import { documentsApi } from '../services/documents';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILES = 20;
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

interface FileWithProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
  documentId?: string;
}

export interface DocumentUploadModalProps {
  caseId?: string;
  cases?: Case[];
  onUploadComplete: (documentIds: string[], assemblyId?: string) => void;
  onClose: () => void;
}

/**
 * Modal for uploading documents with drag-and-drop support.
 * Supports batch upload, assembly option, and per-file progress tracking.
 * 
 * Requirements: 2.1.1.1, 2.1.1.2, 2.1.1.3, 2.1.1.4, 2.1.3.1
 */
export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  caseId: initialCaseId,
  cases = [],
  onUploadComplete,
  onClose,
}) => {
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string>(initialCaseId || '');
  const [assembleAsOne, setAssembleAsOne] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File "${file.name}" exceeds maximum size of 50MB`;
    }
    if (!ALLOWED_TYPES.includes(file.type) && !file.type.startsWith('image/')) {
      return `File "${file.name}" has unsupported type`;
    }
    return null;
  };

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    
    if (files.length + fileArray.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const validatedFiles: FileWithProgress[] = [];
    const errors: string[] = [];

    fileArray.forEach(file => {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
      } else {
        // Check for duplicates
        const isDuplicate = files.some(f => f.file.name === file.name && f.file.size === file.size);
        if (!isDuplicate) {
          validatedFiles.push({
            file,
            progress: 0,
            status: 'pending',
          });
        }
      }
    });

    if (errors.length > 0) {
      setError(errors.join('. '));
    } else {
      setError(null);
    }

    if (validatedFiles.length > 0) {
      setFiles(prev => [...prev, ...validatedFiles]);
    }
  }, [files]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const retryFile = async (index: number) => {
    const fileWithProgress = files[index];
    if (fileWithProgress.status !== 'error') return;

    setFiles(prev => prev.map((f, i) => 
      i === index ? { ...f, status: 'pending', error: undefined, progress: 0 } : f
    ));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setError(null);

    // Update all files to uploading status
    setFiles(prev => prev.map(f => ({ ...f, status: 'uploading' as const })));

    try {
      const result = await documentsApi.uploadDocuments(
        files.map(f => f.file),
        {
          caseId: selectedCaseId || undefined,
          assembleAsOne,
          onProgress: (fileIndex, progress) => {
            setFiles(prev => prev.map((f, i) => 
              i === fileIndex ? { ...f, progress } : f
            ));
          },
          onFileComplete: (fileIndex, documentId) => {
            setFiles(prev => prev.map((f, i) => 
              i === fileIndex ? { ...f, status: 'complete', progress: 100, documentId } : f
            ));
          },
        }
      );

      onUploadComplete(result.documentIds, result.assemblyId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      setFiles(prev => prev.map(f => 
        f.status === 'uploading' ? { ...f, status: 'error', error: errorMessage } : f
      ));
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusColor = (status: FileWithProgress['status']): string => {
    switch (status) {
      case 'complete': return '#4caf50';
      case 'error': return '#f44336';
      case 'uploading': return '#1976d2';
      default: return '#9e9e9e';
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => e.target === e.currentTarget && !uploading && onClose()}
      data-testid="document-upload-modal"
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>Upload Documents</h2>
          <button
            onClick={onClose}
            disabled={uploading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: uploading ? 'not-allowed' : 'pointer',
              color: '#666',
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
            }}
            data-testid="upload-error"
          >
            {error}
          </div>
        )}

        {/* Case selector */}
        {cases.length > 0 && !initialCaseId && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
              Assign to Case (optional)
            </label>
            <select
              value={selectedCaseId}
              onChange={(e) => setSelectedCaseId(e.target.value)}
              disabled={uploading}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
              data-testid="case-selector"
            >
              <option value="">Upload to Inbox (no case)</option>
              {cases.map(c => (
                <option key={c.caseId} value={c.caseId}>
                  {c.childLastName}, {c.childFirstName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Drag and drop zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragging ? '#1976d2' : '#ddd'}`,
            borderRadius: '8px',
            padding: '32px',
            textAlign: 'center',
            backgroundColor: isDragging ? '#e3f2fd' : '#fafafa',
            cursor: uploading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            marginBottom: '16px',
          }}
          data-testid="drop-zone"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept={ALLOWED_TYPES.join(',')}
            disabled={uploading}
            data-testid="file-input"
          />
          <div style={{ fontSize: '48px', marginBottom: '8px' }}>📄</div>
          <p style={{ margin: 0, color: '#666' }}>
            {isDragging
              ? 'Drop files here'
              : 'Drag and drop files here, or click to select'}
          </p>
          <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#999' }}>
            Max {MAX_FILES} files, up to 50MB each
          </p>
        </div>

        {/* Assemble toggle */}
        {files.length > 1 && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={assembleAsOne}
                onChange={(e) => setAssembleAsOne(e.target.checked)}
                disabled={uploading}
                data-testid="assemble-toggle"
              />
              <span>Combine as single document</span>
            </label>
            <p style={{ margin: '4px 0 0 24px', fontSize: '12px', color: '#666' }}>
              Treat all files as pages of one document (e.g., scanned pages)
            </p>
          </div>
        )}

        {/* File list */}
        {files.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ margin: '0 0 8px' }}>Files ({files.length})</h4>
            <div style={{ maxHeight: '200px', overflow: 'auto' }}>
              {files.map((f, index) => (
                <div
                  key={`${f.file.name}-${index}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px',
                    borderBottom: '1px solid #eee',
                    gap: '12px',
                  }}
                  data-testid={`file-item-${index}`}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontWeight: 500, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}>
                      {f.file.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {formatFileSize(f.file.size)}
                    </div>
                    {f.status === 'uploading' && (
                      <div style={{ marginTop: '4px' }}>
                        <div
                          style={{
                            height: '4px',
                            backgroundColor: '#e0e0e0',
                            borderRadius: '2px',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              width: `${f.progress}%`,
                              backgroundColor: '#1976d2',
                              transition: 'width 0.2s',
                            }}
                            data-testid={`progress-bar-${index}`}
                          />
                        </div>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                          {f.progress}%
                        </div>
                      </div>
                    )}
                    {f.error && (
                      <div style={{ fontSize: '12px', color: '#f44336', marginTop: '4px' }}>
                        {f.error}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(f.status),
                      }}
                      title={f.status}
                    />
                    {f.status === 'error' && (
                      <button
                        onClick={() => retryFile(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#1976d2',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                        data-testid={`retry-button-${index}`}
                      >
                        Retry
                      </button>
                    )}
                    {!uploading && f.status !== 'complete' && (
                      <button
                        onClick={() => removeFile(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#f44336',
                          cursor: 'pointer',
                          fontSize: '18px',
                          padding: '0 4px',
                        }}
                        aria-label={`Remove ${f.file.name}`}
                        data-testid={`remove-button-${index}`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={uploading}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: uploading ? 'not-allowed' : 'pointer',
            }}
            data-testid="cancel-button"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || files.length === 0}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: files.length === 0 ? '#ccc' : '#1976d2',
              color: 'white',
              cursor: uploading || files.length === 0 ? 'not-allowed' : 'pointer',
            }}
            data-testid="upload-button"
          >
            {uploading ? 'Uploading...' : `Upload ${files.length} file${files.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
